// ============================================================
// HOOKED ENGINE — FISH ART              [v2 overhaul — new]
// Procedural, layered SVG creatures. Until real .webp art lands
// on the CDN, every fish still reads as a designed thing — not a
// system-font emoji. Keyed by ILK (silhouette + aura) and TIER
// (detail + fin count), so the four Ilks are visually distinct
// at a glance and tier reads as menace.
//
// This is the GLYPH-FALLBACK tier only. The resolver law is
// untouched: real fish_{id}.webp still wins in Sprite/Sprite
// Manifest. This file is what shows when it 404s — and now that
// fallback is a feature, not an apology.
//
// LAWS IN FORCE:
//   useId() per instance — SVG gradient/filter IDs MUST be
//   unique or simultaneous fish on screen corrupt each other's
//   filters (the holofoil multi-instance trap).
//   Palette law — pure WORLD colors; ilkAura() is the one source.
// ============================================================

import { useId } from "react";
import { SD, ilkAura } from "./theme";

// Per-Ilk body silhouette + accent. Content differs, not just hue.
const ILK_SHAPE = {
  Space: {
    // sleek dart, forked tail, star-fleck belly
    body: "M18,50 C34,30 66,30 86,44 C94,49 94,51 86,56 C66,70 34,70 18,50 Z",
    tail: "M14,50 L2,38 L8,50 L2,62 Z",
    finTop: "M50,36 Q58,22 66,34 Z",
    finBot: "M50,64 Q58,78 66,66 Z",
    fleck: true,
  },
  Phantom: {
    // wispy koi-shade, trailing veil tail
    body: "M20,50 C36,32 64,32 82,46 C90,50 90,50 82,54 C64,68 36,68 20,50 Z",
    tail: "M18,50 C4,40 2,50 6,50 C2,50 4,60 18,50 Z",
    finTop: "M48,34 Q56,18 64,32 Z",
    finBot: "M48,66 Q56,82 64,68 Z",
    veil: true,
  },
  Dragon: {
    // armored brawler, spined dorsal, heavy jaw
    body: "M18,50 C32,28 70,30 88,46 C95,50 95,50 88,54 C70,70 32,72 18,50 Z",
    tail: "M14,50 L0,36 L7,50 L0,64 Z",
    finTop: "M40,34 L48,22 L54,34 L62,24 L66,36 Z",
    finBot: "M50,66 Q58,80 66,66 Z",
    spine: true,
  },
  Time: {
    // slow ancient thing, round body, twin trailing filaments
    body: "M22,50 C36,34 64,34 80,48 C88,50 88,50 80,52 C64,66 36,66 22,50 Z",
    tail: "M20,50 L6,42 L12,50 L6,58 Z",
    finTop: "M50,36 Q57,24 64,35 Z",
    finBot: "M50,64 Q57,76 64,65 Z",
    rings: true,
  },
};

export function FishArt({ fish, size = 96, animate = true, facing = 1 }) {
  const uid = useId().replace(/:/g, "");
  const ilk = fish?.ilk || "Space";
  const shape = ILK_SHAPE[ilk] || ILK_SHAPE.Space;
  const base = fish?.color || ilkAura(ilk);
  const aura = ilkAura(ilk);
  const tier = Math.min(fish?.tier || 1, 4);

  const gBody = `bodyGrad-${uid}`;
  const gGlow = `glow-${uid}`;
  const gEye = `eye-${uid}`;

  const swayStyle = animate ? { animation: "finSway 3.2s ease-in-out infinite", transformOrigin: "10px 50px" } : {};

  return (
    <div style={{
      width: size, height: size, position: "relative",
      transform: facing < 0 ? "scaleX(-1)" : "none",
      animation: animate ? "fishHover 4s ease-in-out infinite" : "none",
    }}>
      <svg viewBox="0 0 100 100" width={size} height={size} style={{ overflow: "visible" }}>
        <defs>
          <radialGradient id={gBody} cx="42%" cy="38%" r="72%">
            <stop offset="0%" stopColor="#ffffff" stopOpacity="0.85" />
            <stop offset="26%" stopColor={base} stopOpacity="1" />
            <stop offset="78%" stopColor={base} stopOpacity="0.92" />
            <stop offset="100%" stopColor={SD.waterDeep} stopOpacity="1" />
          </radialGradient>
          <radialGradient id={gGlow} cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor={aura} stopOpacity="0.55" />
            <stop offset="60%" stopColor={aura} stopOpacity="0.18" />
            <stop offset="100%" stopColor={aura} stopOpacity="0" />
          </radialGradient>
          <radialGradient id={gEye} cx="40%" cy="40%" r="60%">
            <stop offset="0%" stopColor="#fff" />
            <stop offset="55%" stopColor={SD.void2} />
            <stop offset="100%" stopColor="#000" />
          </radialGradient>
        </defs>

        {/* aura halo — the Ilk's tell */}
        <ellipse cx="50" cy="50" rx="52" ry="40" fill={`url(#${gGlow})`}
          style={animate ? { animation: "moonBreathe 5s ease-in-out infinite" } : {}} />

        {/* tail (sways) */}
        <g style={swayStyle}>
          <path d={shape.tail} fill={base} opacity="0.9" stroke={aura} strokeWidth="0.6" />
        </g>

        {/* Phantom trailing veil */}
        {shape.veil && (
          <path d="M18,50 C0,44 -6,50 -2,50 C-8,50 0,58 18,50 Z" fill={aura} opacity="0.25"
            style={animate ? { animation: "finSway 4.5s ease-in-out infinite", transformOrigin: "8px 50px" } : {}} />
        )}

        {/* fins */}
        <path d={shape.finTop} fill={base} opacity="0.85" stroke={aura} strokeWidth="0.5" />
        <path d={shape.finBot} fill={base} opacity="0.85" stroke={aura} strokeWidth="0.5" />

        {/* Dragon dorsal spines already in finTop; add tier ridges */}
        {shape.spine && tier >= 2 && (
          <path d="M30,40 L34,30 L38,40 M40,38 L44,28 L48,38" fill="none" stroke={aura} strokeWidth="1.4" strokeLinecap="round" />
        )}

        {/* body */}
        <path d={shape.body} fill={`url(#${gBody})`} stroke={aura} strokeWidth="0.8" />

        {/* scale sheen line */}
        <path d="M30,45 C46,38 64,38 78,46" fill="none" stroke="#ffffff" strokeWidth="1" strokeOpacity="0.4" strokeLinecap="round" />

        {/* Space flecks / Time rings — tier-scaled detail */}
        {shape.fleck && Array.from({ length: 2 + tier }).map((_, k) => (
          <circle key={k} cx={40 + k * 9} cy={52 + (k % 2) * 4} r="0.9" fill="#fff" opacity="0.75" />
        ))}
        {shape.rings && Array.from({ length: tier }).map((_, k) => (
          <ellipse key={k} cx={52 - k * 6} cy="50" rx={4 + k * 3} ry={3 + k * 2} fill="none" stroke={aura} strokeWidth="0.7" opacity={0.5 - k * 0.1} />
        ))}

        {/* eye */}
        <circle cx="76" cy="47" r="4.4" fill={`url(#${gEye})`} stroke={aura} strokeWidth="0.5" />
        <circle cx="74.6" cy="45.6" r="1.3" fill="#fff" />

        {/* tier menace: legend gets a crown-glint */}
        {fish?.legend && (
          <path d="M46,26 L50,18 L54,26 L58,20 L60,28 Z" fill={SD.goldBright} opacity="0.9"
            style={animate ? { animation: "starTwinkle 2s ease-in-out infinite" } : {}} />
        )}
      </svg>
    </div>
  );
}

// A tiny inline version for the strike-ring "eyes below" peek etc.
export function FishGlyphMini({ fish, size = 22 }) {
  return <FishArt fish={fish} size={size} animate={false} />;
}
